import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import JobClosingSurveyModal from '../../jobs/components/JobClosingSurveyModal';
import QuickCloseMenu from '../../jobs/components/QuickCloseMenu';
import { useJobClosingSurvey } from '../../jobs/hooks/useJobClosingSurvey';
import { Job } from '../../jobs/types/JobClosingSurvey';

/**
 * Integration component that replaces the existing JavaScript-based Job Closing Survey
 * Handles both the modal rendering and the button event listeners
 */
const JobClosingSurveyIntegration: React.FC = () => {
  const {
    isVisible,
    currentJob,
    showSurvey,
    hideSurvey,
    handleSuccess,
    handleError
  } = useJobClosingSurvey();

  const [quickCloseJob, setQuickCloseJob] = useState<Job | null>(null);
  const [userSkipPreference, setUserSkipPreference] = useState<boolean>(false);

  // Use ref to hold the latest user skip preference value
  // This ensures the event listener always has access to the current value
  const userSkipPreferenceRef = useRef<boolean>(false);

  // Fetch user preference on mount
  useEffect(() => {
    const fetchUserPreference = () => {
      try {
        // Get the current user's skip preference from the DOM
        const userDataElement = document.querySelector('[data-user-skip-survey]');
        console.log('User data element:', userDataElement);
        if (userDataElement) {
          const skipSurveyValue = userDataElement.getAttribute('data-user-skip-survey');
          console.log('Skip survey value from DOM:', skipSurveyValue);
          const skipSurvey = skipSurveyValue === 'true';
          console.log('Setting user skip preference to:', skipSurvey);
          setUserSkipPreference(skipSurvey);
        } else {
          console.log('No user data element found in DOM');
        }
      } catch (error) {
        console.error('Error fetching user preference:', error);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(fetchUserPreference, 100);
  }, []);

  // Update ref whenever preference changes
  useEffect(() => {
    userSkipPreferenceRef.current = userSkipPreference;
  }, [userSkipPreference]);


  useEffect(() => {
    // Handle close job button clicks
    const handleCloseJobClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest('[data-job-close="true"]') as HTMLElement;

      if (button) {
        event.preventDefault();

        const jobId = parseInt(button.getAttribute('data-job-id') || '0');
        const jobTitle = button.getAttribute('data-job-title') || '';

        // Use ref to get latest preference value (not state which could be stale in closure)
        console.log('Close button clicked:', {
          jobId,
          jobTitle,
          buttonElement: button,
          userSkipPreference: userSkipPreferenceRef.current
        });

        if (jobId) {
          const job: Job = {
            id: jobId,
            job_title: jobTitle || 'Job #' + jobId,
            created_at: new Date().toISOString()
          };

          // Check user preference using ref (always has latest value)
          if (userSkipPreferenceRef.current) {
            // Show quick close modal
            console.log('Showing quick close modal for job:', job);
            setQuickCloseJob(job);
          } else {
            // Show full survey modal
            console.log('Showing survey for job:', job);
            showSurvey(job);
          }
        }
      }
    };

    // Normal event listener is fine now
    document.addEventListener('click', handleCloseJobClick);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleCloseJobClick);
    };
  }, [showSurvey]);  // Removed userSkipPreference from deps - using ref instead

  // Hide the original Bootstrap modal if it exists
  useEffect(() => {
    const originalModal = document.getElementById('jobClosingSurveyModal');
    if (originalModal) {
      originalModal.style.display = 'none';
    }
  }, []);

  return (
    <div className="job-closing-survey-integration">
      {currentJob && (
        <JobClosingSurveyModal
          job={currentJob}
          isVisible={isVisible}
          onClose={hideSurvey}
          onSuccess={(message) => {
            handleSuccess(message);
            // The page will reload after successful submission, so no need to update state here
          }}
          onError={handleError}
        />
      )}
      
      {quickCloseJob && (
        <QuickCloseMenu
          job={quickCloseJob}
          isOpen={!!quickCloseJob}
          onClose={() => setQuickCloseJob(null)}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      )}
      
    </div>
  );
};

export default JobClosingSurveyIntegration;